import { Alert, Box, MenuItem, Snackbar, Typography } from '@mui/material';
import React, { useEffect } from 'react';
import { snackBar } from 'src/content/keywordResearch/keywordOverview/keywordOverviewCSS';
import { websiteAuditAfterLinearBar } from 'src/content/siteAudit/siteAuditCSS';
import LightbulbCircleRoundedIcon from '@mui/icons-material/LightbulbCircleRounded';
import { useSelector } from 'react-redux';
import { RootState } from 'src/redux/store';

interface IDisconnectGSCReducerState {
  isOpen: boolean;
  isLoading: boolean;
  snacksBarVisibility: boolean;
  snacksBarSuccess: boolean;
  snacksBarMessage: string;
}

interface IDisconnectGSCReducerActionTypes {
  type: 'updateState';
  payload: Partial<IDisconnectGSCReducerState>;
}

const initialState: IDisconnectGSCReducerState = {
  isOpen: false,
  isLoading: false,
  snacksBarVisibility: false,
  snacksBarSuccess: false,
  snacksBarMessage: ''
};

interface DisconnectGSCContextProps {
  disconnectGSCState: IDisconnectGSCReducerState;
  disconnectGSCAction: React.Dispatch<IDisconnectGSCReducerActionTypes>;
}

const reducer = (
  state: IDisconnectGSCReducerState,
  action: IDisconnectGSCReducerActionTypes
) => {
  switch (action.type) {
    case 'updateState':
      return {
        ...state,
        ...action.payload
      };
    default:
      return state;
  }
};

const DisconnectGSCContext = React.createContext<
  DisconnectGSCContextProps | undefined
>(undefined);

interface DisconnectGSCProviderProps {
  children: React.ReactNode;
}

export const DisconnectGSCProvider: React.FC<DisconnectGSCProviderProps> = ({
  children
}) => {
  const [state, dispatch] = React.useReducer(reducer, initialState);
  return (
    <DisconnectGSCContext.Provider
      value={{ disconnectGSCAction: dispatch, disconnectGSCState: state }}
    >
      {children}
    </DisconnectGSCContext.Provider>
  );
};

export const useDisconnectGSC = (): DisconnectGSCContextProps => {
  const context = React.useContext(DisconnectGSCContext);
  if (!context) {
    throw new Error(
      'useDisconnectGSC must be used within a DisconnectGSCProvider'
    );
  }
  return context;
};

export const DisconnectGSCButton = ({ handleClose, keys }) => {
  const { disconnectGSCState, disconnectGSCAction } = useDisconnectGSC();
  const [isBtnDisabled, setIsBtnDisabled] = React.useState(false);

  let siteDetails: any =
    useSelector((state: RootState) => state.siteAudit.siteAuditDetails) || null;
  const siteSoftDeleted = useSelector(
    (state: RootState) => state?.siteAudit?.isSiteSoftDeleted
  );

  useEffect(() => {
    if (siteDetails) {
      if (siteDetails?.is_gsc) {
        setIsBtnDisabled(false);
      } else {
        setIsBtnDisabled(true);
      }
    } else {
      setIsBtnDisabled(false);
    }
  }, [siteDetails]);

  const handleButtonClick = (value: boolean) => {
    disconnectGSCAction({
      type: 'updateState',
      payload: {
        isOpen: value
      }
    });
  };

  return (
    <MenuItem
      disabled={isBtnDisabled || siteSoftDeleted}
      onClick={() => {
        handleButtonClick(true);
        handleClose();
      }}
      key={keys}
    >
      <Typography sx={websiteAuditAfterLinearBar.deleteAudit}>
        Disconnect GSC
      </Typography>
    </MenuItem>
  );
};

export const DisconnectGSCSnaksbar = () => {
  const { disconnectGSCState, disconnectGSCAction } = useDisconnectGSC();

  useEffect(() => {
    if (disconnectGSCState.snacksBarVisibility) {
      setTimeout(() => {
        disconnectGSCAction({
          type: 'updateState',
          payload: {
            snacksBarVisibility: false,
            snacksBarSuccess: false,
            snacksBarMessage: ''
          }
        });
      }, 5000);
    }
  }, [disconnectGSCState]);

  return (
    <>
      {disconnectGSCState?.snacksBarMessage && (
        <Snackbar
          sx={{ marginTop: '2.8rem' }}
          anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
          open={disconnectGSCState.snacksBarVisibility}
        >
          <Box>
            <Alert
              icon={
                disconnectGSCState.snacksBarSuccess ? (
                  <img
                    draggable={false}
                    src={
                      process.env.PUBLIC_URL +
                      `/static/images/icons/checkIcon.svg`
                    }
                  />
                ) : (
                  <LightbulbCircleRoundedIcon fontSize="inherit" />
                )
              }
              severity="error"
              variant="filled"
              sx={
                disconnectGSCState.snacksBarSuccess
                  ? snackBar?.alert
                  : { background: 'var(--color-error-bg)', color: 'var(--color-primary)' }
              }
            >
              <p>{disconnectGSCState.snacksBarMessage}</p>
            </Alert>
          </Box>
        </Snackbar>
      )}
    </>
  );
};
